/**
 * Retry utilities with exponential backoff for handling transient API failures (currently used in Maps API calls)
 */

export interface RetryOptions {
  /**
   * Maximum number of retry attempts (default: 3)
   */
  maxRetries?: number;

  /**
   * Initial delay in milliseconds before first retry (default: 1000ms)
   */
  initialDelayMs?: number;

  /**
   * Backoff multiplier for exponential delay (default: 2)
   * Delay will be: initialDelay * (backoffMultiplier ^ attemptNumber)
   */
  backoffMultiplier?: number;

  /**
   * Maximum delay in milliseconds to cap exponential growth (default: 10000ms)
   */
  maxDelayMs?: number;

  /**
   * Predicate to determine if an error should trigger a retry
   * If not provided, all errors will be retried
   * @param error The error that occurred
   * @param attempt Current attempt number (0-indexed)
   * @returns true if should retry, false otherwise
   */
  shouldRetry?: (error: Error, attempt: number) => boolean;

  /**
   * Callback invoked before each retry attempt
   * Useful for logging or tracking
   * @param error The error that caused the retry
   * @param attempt Current attempt number (0-indexed)
   * @param delayMs Delay before this retry attempt
   */
  onRetry?: (error: Error, attempt: number, delayMs: number) => void;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'onRetry' | 'shouldRetry'>> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 10000,
};

/**
 * Delays execution for the specified number of milliseconds
 * @param ms Milliseconds to delay
 * @returns Promise that resolves after the delay
 */
const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Calculates the delay for a given retry attempt using exponential backoff
 * @param attempt Current attempt number (0-indexed)
 * @param options Retry options
 * @returns Delay in milliseconds
 */
const calculateDelay = (attempt: number, options: Required<Omit<RetryOptions, 'onRetry' | 'shouldRetry'>>): number => {
  const exponentialDelay = options.initialDelayMs * options.backoffMultiplier ** attempt;
  return Math.min(exponentialDelay, options.maxDelayMs);
};

/**
 * Executes an async function with retry logic and exponential backoff
 *
 * @param fn Async function to execute
 * @param options Retry configuration options
 * @returns Promise that resolves with the function result or rejects with the last error
 *
 * @example
 * ```typescript
 * const fetchData = async () => {
 *   const response = await fetch('/api/data');
 *   if (!response.ok) throw new Error('Failed to fetch');
 *   return response.json();
 * };
 *
 * const data = await retryWithBackoff(fetchData, {
 *   maxRetries: 3,
 *   initialDelayMs: 1000,
 *   onRetry: (error, attempt, delay) => {
 *     console.log(`Retry ${attempt + 1} after ${delay}ms due to:`, error.message);
 *   },
 * });
 * ```
 */
// Retry logic requires sequential awaits for exponential backoff - ESLint exemptions justified
/* eslint-disable no-await-in-loop, no-plusplus */
export const retryWithBackoff = async <T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      // First attempt (attempt === 0) executes immediately
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // If this was the last attempt, throw the error
      if (attempt === opts.maxRetries) {
        throw lastError;
      }

      // Check if we should retry this error
      if (options.shouldRetry && !options.shouldRetry(lastError, attempt)) {
        throw lastError;
      }

      // Calculate delay for next attempt
      const delayMs = calculateDelay(attempt, opts);

      // Invoke retry callback if provided
      if (options.onRetry) {
        options.onRetry(lastError, attempt, delayMs);
      }

      // Wait before next attempt (intentionally sequential for retry backoff)
      await delay(delayMs);
    }
  }

  // This should never be reached due to the throw in the loop, but TypeScript needs it
  throw lastError || new Error('Unknown error occurred during retry');
};
/* eslint-enable no-await-in-loop, no-plusplus */

/**
 * Default retry predicate for HTTP errors
 * Retries on network errors and 5xx server errors, but not on 4xx client errors
 *
 * @param error The error to check
 * @returns true if the error should trigger a retry
 */
export const shouldRetryHttpError = (error: Error): boolean => {
  // Retry on network errors (TypeError, fetch failures)
  if (error.name === 'TypeError' || error.message.includes('network')) {
    return true;
  }

  // Check for HTTP status code in error message (axios, fetch, etc.)
  const statusMatch = error.message.match(/status\s*(?:code)?\s*(\d+)/i);
  if (statusMatch) {
    const statusCode = parseInt(statusMatch[1], 10);
    // Retry on 5xx server errors, but not on 4xx client errors
    return statusCode >= 500 && statusCode < 600;
  }

  // Retry on timeout errors
  if (error.message.toLowerCase().includes('timeout')) {
    return true;
  }

  // Don't retry by default for unknown errors
  return false;
};
